import React, { useState, useEffect } from 'react';
import { Header, SpaceBetween, Spinner, Alert, Select, FormField, Button, Modal, Box, Table, Input } from '@amzn/awsui-components-react';
import DataContentTable from '../component/dataContentTable';
import LiquidityEngineAPIClient from './liquidityEngineAPIClient';
import { buildColumnDefinitions, buildContentSelectorOptions, buildFilterOptions, SETTLEMENT_FIELDS } from './icSettlementsConfig';

// All 15 settlement statuses from the Smithy model.
// Each entry has a 'value' (what we send to the API) and a 'label' (what the user sees).
const SETTLEMENT_STATUS = {
    INITIATED: 'INITIATED',
    VALIDATING: 'VALIDATING',
    ENRICHING: 'ENRICHING',
    ENRICHMENT_COMPLETE: 'ENRICHMENT_COMPLETE',
    CHECKING_LIQUIDITY: 'CHECKING_LIQUIDITY',
    PASSED_LIQUIDITY_CHECK: 'PASSED_LIQUIDITY_CHECK',
    APPROVED_BY_TREASURY: 'APPROVED_BY_TREASURY',
    EXPORTING_PAYMENT: 'EXPORTING_PAYMENT',
    EXPORTED: 'EXPORTED',
    PAYMENT_EXECUTED: 'PAYMENT_EXECUTED',
    FAILED_VALIDATION: 'FAILED_VALIDATION',
    FAILED_ENRICHMENT: 'FAILED_ENRICHMENT',
    FAILED_LIQUIDITY_CHECK: 'FAILED_LIQUIDITY_CHECK',
    REJECTED_BY_TREASURY: 'REJECTED_BY_TREASURY',
    PAYMENT_FAILED_EXECUTION: 'PAYMENT_FAILED_EXECUTION',
} as const;

const STATUS_OPTIONS = [
    { value: SETTLEMENT_STATUS.INITIATED, label: 'Initiated' },
    { value: SETTLEMENT_STATUS.VALIDATING, label: 'Validating' },
    { value: SETTLEMENT_STATUS.ENRICHING, label: 'Enriching' },
    { value: SETTLEMENT_STATUS.ENRICHMENT_COMPLETE, label: 'Enrichment Complete' },
    { value: SETTLEMENT_STATUS.CHECKING_LIQUIDITY, label: 'Checking Liquidity' },
    { value: SETTLEMENT_STATUS.PASSED_LIQUIDITY_CHECK, label: 'Passed Liquidity Check' },
    { value: SETTLEMENT_STATUS.APPROVED_BY_TREASURY, label: 'Approved by Treasury' },
    { value: SETTLEMENT_STATUS.EXPORTING_PAYMENT, label: 'Exporting Payment' },
    { value: SETTLEMENT_STATUS.EXPORTED, label: 'Exported' },
    { value: SETTLEMENT_STATUS.PAYMENT_EXECUTED, label: 'Payment Executed' },
    { value: SETTLEMENT_STATUS.FAILED_VALIDATION, label: 'Failed Validation' },
    { value: SETTLEMENT_STATUS.FAILED_ENRICHMENT, label: 'Failed Enrichment' },
    { value: SETTLEMENT_STATUS.FAILED_LIQUIDITY_CHECK, label: 'Failed Liquidity Check' },
    { value: SETTLEMENT_STATUS.REJECTED_BY_TREASURY, label: 'Rejected by Treasury' },
    { value: SETTLEMENT_STATUS.PAYMENT_FAILED_EXECUTION, label: 'Payment Failed Execution' },
];

const DEFAULT_STATUS = STATUS_OPTIONS.find(o => o.value === SETTLEMENT_STATUS.PASSED_LIQUIDITY_CHECK) || STATUS_OPTIONS[0];

const ICSettlements = () => {
    const [settlements, setSettlements] = useState<Record<string, unknown>[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState(DEFAULT_STATUS);
    const [selectedSettlement, setSelectedSettlement] = useState<Record<string, unknown> | null>(null);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [viewDocsModalVisible, setViewDocsModalVisible] = useState(false);
    const [attachments, setAttachments] = useState<Record<string, unknown>[]>([]);
    const [loadingAttachments, setLoadingAttachments] = useState(false);
    const [attachmentsError, setAttachmentsError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [actionSuccess, setActionSuccess] = useState<string | null>(null);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [rejectError, setRejectError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [createForm, setCreateForm] = useState({
        sendingCo: '',
        receivingCo: '',
        targetAmount: '',
        targetCurrency: '',
        ocmMapping: '',
        valueDate: '',
    });

    useEffect(() => {
        let cancelled = false;
        const fetchSettlements = async () => {
            setLoading(true);
            setError(null);
            try {
                const allSettlements: Record<string, unknown>[] = [];
                let paginationToken: string | null = null;
                let pageCount = 0;
                const MAX_PAGES = 50;
                do {
                    const params: Record<string, string> = { status: selectedStatus.value };
                    if (paginationToken) {
                        params.paginationToken = paginationToken;
                    }
                    const data = await LiquidityEngineAPIClient.invoke('GET', 'settlements', params);
                    if (cancelled) return;
                    allSettlements.push(...(data.settlements || []));
                    paginationToken = data.paginationToken || null;
                    pageCount++;
                    if (pageCount >= MAX_PAGES) {
                        break;
                    }
                } while (paginationToken);
                setSettlements(allSettlements);
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Unknown error');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        fetchSettlements();
        return () => { cancelled = true; };
    }, [selectedStatus, refreshKey]);

    const handleUpload = async () => {
        if (!selectedFile || !selectedSettlement) return;
        setUploading(true);
        setUploadError(null);
        try {
            const settlementId = selectedSettlement[SETTLEMENT_FIELDS.settlementId.key] as string;
            // Step A: Call AttachFiles API to get pre-signed upload URL
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const response = await (LiquidityEngineAPIClient as any).invoke(
                'POST',
                `settlements/${settlementId}/attachments`,
                {},
                { fileName: selectedFile.name }
            );
            // Step B: Upload file directly to S3 using the pre-signed URL
            const uploadResponse = await fetch(response.presignedUploadUrl, {
                method: 'PUT',
                body: selectedFile,
            });
            if (!uploadResponse.ok) {
                throw new Error('Failed to upload file to S3');
            }
            // Success — close modal and reset
            setUploadModalVisible(false);
            setSelectedFile(null);
        } catch (err) {
            setUploadError(err instanceof Error ? err.message : 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const fetchAttachments = async () => {
        if (!selectedSettlement) return;
        setLoadingAttachments(true);
        setAttachmentsError(null);
        try {
            const settlementId = selectedSettlement[SETTLEMENT_FIELDS.settlementId.key] as string;
            const data = await LiquidityEngineAPIClient.invoke('GET', `settlements/${settlementId}/attachments`);
            setAttachments(data.attachments || []);
        } catch (err) {
            setAttachmentsError(err instanceof Error ? err.message : 'Failed to load attachments');
        } finally {
            setLoadingAttachments(false);
        }
    };

    const handleViewDocuments = () => {
        setViewDocsModalVisible(true);
        fetchAttachments();
    };

    const handleDownload = async (attachmentId: string) => {
        if (!selectedSettlement) return;
        try {
            const settlementId = selectedSettlement[SETTLEMENT_FIELDS.settlementId.key] as string;
            const data = await LiquidityEngineAPIClient.invoke('GET', `settlements/${settlementId}/attachments/${attachmentId}`);
            window.open(data.presignedUrl || data.downloadUrl, '_blank');
        } catch (err) {
            setAttachmentsError(err instanceof Error ? err.message : 'Download failed');
        }
    };

    const handleDelete = async (attachmentId: string) => {
        if (!selectedSettlement) return;
        try {
            const settlementId = selectedSettlement[SETTLEMENT_FIELDS.settlementId.key] as string;
            await LiquidityEngineAPIClient.invoke('DELETE', `settlements/${settlementId}/attachments/${attachmentId}`);
            fetchAttachments(); // Refresh the list after delete
        } catch (err) {
            setAttachmentsError(err instanceof Error ? err.message : 'Delete failed');
        }
    };

    // Helper: get the status of the currently selected settlement
    const selectedSettlementStatus = selectedSettlement
        ? (selectedSettlement[SETTLEMENT_FIELDS.status.key] as string)
        : '';

    // Generic action handler — calls an API endpoint, then refreshes the list
    const handleAction = async (action: string) => {
        if (!selectedSettlement) return;
        setActionLoading(action);
        setActionError(null);
        setActionSuccess(null);
        try {
            const settlementId = selectedSettlement[SETTLEMENT_FIELDS.settlementId.key] as string;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (LiquidityEngineAPIClient as any).invoke('POST', `settlements/${settlementId}/${action}`);
            setSelectedSettlement(null);
            // Increment refreshKey to re-trigger the useEffect fetch
            setRefreshKey(k => k + 1);
            setActionSuccess(`Settlement ${action.replace(/-/g, ' ')} completed successfully.`);
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Action failed, please try again.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async () => {
        if (!selectedSettlement || !rejectionReason.trim()) return;
        setActionLoading('reject');
        setActionError(null);
        setActionSuccess(null);
        setRejectError(null);
        try {
            const settlementId = selectedSettlement[SETTLEMENT_FIELDS.settlementId.key] as string;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (LiquidityEngineAPIClient as any).invoke(
                'POST', `settlements/${settlementId}/reject`, {}, { rejectionReason: rejectionReason.trim() }
            );
            setRejectModalVisible(false);
            setRejectionReason('');
            setSelectedSettlement(null);
            setRefreshKey(k => k + 1);
            setActionSuccess('Settlement rejected successfully.');
        } catch (err) {
            setRejectError(err instanceof Error ? err.message : 'Reject failed, please try again.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleCreateSettlement = async () => {
        const { sendingCo, receivingCo, targetAmount, targetCurrency, ocmMapping } = createForm;
        if (!sendingCo || !receivingCo || !targetAmount || !targetCurrency || !ocmMapping) return;
        setCreateLoading(true);
        setCreateError(null);
        try {
            const settlementId = `il-${crypto.randomUUID().slice(0, 8)}`;
            const body: Record<string, string> = { settlementId, ...createForm };
            // Remove empty optional fields — DynamoDB rejects empty strings on index keys
            Object.keys(body).forEach(k => { if (!body[k]) delete body[k]; });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (LiquidityEngineAPIClient as any).invoke(
                'POST', 'settlements', {}, body
            );
            setCreateModalVisible(false);
            setCreateForm({ sendingCo: '', receivingCo: '', targetAmount: '', targetCurrency: '', ocmMapping: '', valueDate: '' });
            setRefreshKey(k => k + 1);
            setActionSuccess('Settlement created successfully.');
        } catch (err) {
            setCreateError(err instanceof Error ? err.message : 'Failed to create settlement.');
        } finally {
            setCreateLoading(false);
        }
    };

    return (
        <SpaceBetween size="l">
            <Header variant="h1">IC Settlements</Header>
            <div style={{ maxWidth: 300 }}>
                <FormField label="Status">
                    <Select
                        selectedOption={selectedStatus}
                        onChange={({ detail }) => { setSelectedStatus(detail.selectedOption as typeof DEFAULT_STATUS); setSelectedSettlement(null); setActionSuccess(null); }}
                        options={STATUS_OPTIONS}
                        placeholder="Filter by status"
                    />
                </FormField>
            </div>
            {loading && <Spinner size="large" />}
            {error && <Alert type="error" header="Error">Failed to load settlements, please try again.</Alert>}
            {!loading && !error && (
                <>
                    <SpaceBetween direction="horizontal" size="s">
                        <Button onClick={() => setCreateModalVisible(true)}>Create Settlement</Button>
                        <Button disabled={!selectedSettlement} onClick={() => setUploadModalVisible(true)}>Upload Document</Button>
                        <Button disabled={!selectedSettlement} onClick={handleViewDocuments}>View Documents</Button>
                        <Button
                            disabled={!selectedSettlement || selectedSettlementStatus !== SETTLEMENT_STATUS.PASSED_LIQUIDITY_CHECK || !!actionLoading}
                            loading={actionLoading === 'approve'}
                            onClick={() => handleAction('approve')}
                        >Approve</Button>
                        <Button
                            disabled={!selectedSettlement || selectedSettlementStatus !== SETTLEMENT_STATUS.PASSED_LIQUIDITY_CHECK || !!actionLoading}
                            loading={actionLoading === 'reject'}
                            onClick={() => setRejectModalVisible(true)}
                        >Reject</Button>
                        <Button
                            disabled={!selectedSettlement || selectedSettlementStatus !== SETTLEMENT_STATUS.APPROVED_BY_TREASURY || !!actionLoading}
                            loading={actionLoading === 'export'}
                            onClick={() => handleAction('export')}
                        >Export</Button>
                        <Button
                            disabled={!selectedSettlement || !([SETTLEMENT_STATUS.FAILED_VALIDATION, SETTLEMENT_STATUS.FAILED_ENRICHMENT, SETTLEMENT_STATUS.FAILED_LIQUIDITY_CHECK] as string[]).includes(selectedSettlementStatus) || !!actionLoading}
                            loading={actionLoading === 'redrive'}
                            onClick={() => handleAction('redrive')}
                        >Redrive</Button>
                        <Button
                            disabled={!selectedSettlement || !([SETTLEMENT_STATUS.PASSED_LIQUIDITY_CHECK, SETTLEMENT_STATUS.FAILED_LIQUIDITY_CHECK] as string[]).includes(selectedSettlementStatus) || !!actionLoading}
                            loading={actionLoading === 'rerun-liquidity-check'}
                            onClick={() => handleAction('rerun-liquidity-check')}
                        >Re-run Liquidity Check</Button>
                    </SpaceBetween>
                    {actionError && <Alert type="error" dismissible onDismiss={() => setActionError(null)}>{actionError}</Alert>}
                    {actionSuccess && <Alert type="success" dismissible onDismiss={() => setActionSuccess(null)}>{actionSuccess}</Alert>}
                    <DataContentTable
                        distributions={settlements}
                        columnDefinitions={buildColumnDefinitions()}
                        contentSelectorOptions={buildContentSelectorOptions()}
                        filterOptions={buildFilterOptions()}
                        header={`IC Settlements (${settlements.length})`}
                        trackBy={SETTLEMENT_FIELDS.settlementId.key}
                        selectionType="single"
                        elementSelected={selectedSettlement ? [selectedSettlement] : []}
                        onSelectionChange={({ detail }: { detail: { selectedItems: Record<string, unknown>[] } }) =>
                            setSelectedSettlement(detail.selectedItems[0] || null)
                        }
                    />
                </>
            )}
            <Modal
                visible={uploadModalVisible}
                onDismiss={() => { setUploadModalVisible(false); setSelectedFile(null); setUploadError(null); }}
                header="Upload Single Document"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => { setUploadModalVisible(false); setSelectedFile(null); setUploadError(null); }}>Cancel</Button>
                            <Button variant="primary" loading={uploading} disabled={!selectedFile} onClick={handleUpload}>Upload Document</Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="m">
                    <p>Select one document to upload.</p>
                    <input
                        key={String(uploadModalVisible)}
                        type="file"
                        accept=".pdf,.doc,.docx,.xlsx,.csv,.msg,.png,.jpeg,.htm"
                        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    />
                    {uploadError && <Alert type="error">{uploadError}</Alert>}
                </SpaceBetween>
            </Modal>
            <Modal
                visible={viewDocsModalVisible}
                onDismiss={() => { setViewDocsModalVisible(false); setAttachmentsError(null); }}
                header="Document(s)"
                size="large"
                footer={
                    <Box float="right">
                        <Button variant="link" onClick={() => setViewDocsModalVisible(false)}>Close</Button>
                    </Box>
                }
            >
                {loadingAttachments && <Spinner />}
                {attachmentsError && <Alert type="error">{attachmentsError}</Alert>}
                {!loadingAttachments && !attachmentsError && (
                    <Table
                        items={attachments}
                        columnDefinitions={[
                            { id: 'fileName', header: 'Document Name', cell: (item: Record<string, unknown>) => item.fileName as string },
                            { id: 'uploadedBy', header: 'Uploader', cell: (item: Record<string, unknown>) => item.uploadedBy as string },
                            { id: 'uploadedAt', header: 'Upload Date', cell: (item: Record<string, unknown>) => {
                                if (!item.uploadedAt) return '';
                                const ts = item.uploadedAt as number | string;
                                const date = new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
                                return date.toLocaleString();
                            }},
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (item: Record<string, unknown>) => (
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <Button variant="link" onClick={() => handleDownload(item.attachmentId as string)}>Download</Button>
                                        <Button variant="link" onClick={() => handleDelete(item.attachmentId as string)}>Delete</Button>
                                    </SpaceBetween>
                                ),
                            },
                        ]}
                        empty="No documents attached to this settlement."
                    />
                )}
            </Modal>
            <Modal
                visible={rejectModalVisible}
                onDismiss={() => { setRejectModalVisible(false); setRejectionReason(''); setRejectError(null); }}
                header="Reject Settlement"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => { setRejectModalVisible(false); setRejectionReason(''); setRejectError(null); }}>Cancel</Button>
                            <Button variant="primary" loading={actionLoading === 'reject'} disabled={!rejectionReason.trim()} onClick={handleReject}>Reject</Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <FormField label="Rejection Reason" description="Provide a reason for rejecting this settlement.">
                    <Input value={rejectionReason} onChange={({ detail }) => setRejectionReason(detail.value)} placeholder="Enter reason..." />
                </FormField>
                {rejectError && <Alert type="error">{rejectError}</Alert>}
            </Modal>
            <Modal
                visible={createModalVisible}
                onDismiss={() => { setCreateModalVisible(false); setCreateError(null); }}
                header="Create Settlement"
                size="medium"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => { setCreateModalVisible(false); setCreateError(null); }}>Cancel</Button>
                            <Button variant="primary" loading={createLoading} disabled={!createForm.sendingCo || !createForm.receivingCo || !createForm.targetAmount || !createForm.targetCurrency || !createForm.ocmMapping} onClick={handleCreateSettlement}>Create</Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="m">
                    <FormField label="Sending Company *">
                        <Input value={createForm.sendingCo} onChange={({ detail }) => setCreateForm(f => ({ ...f, sendingCo: detail.value }))} placeholder="e.g. ENTITY01" />
                    </FormField>
                    <FormField label="Receiving Company *">
                        <Input value={createForm.receivingCo} onChange={({ detail }) => setCreateForm(f => ({ ...f, receivingCo: detail.value }))} placeholder="e.g. ENTITY02" />
                    </FormField>
                    <FormField label="Target Amount *">
                        <Input value={createForm.targetAmount} onChange={({ detail }) => setCreateForm(f => ({ ...f, targetAmount: detail.value }))} placeholder="e.g. 1000" />
                    </FormField>
                    <FormField label="Target Currency *">
                        <Input value={createForm.targetCurrency} onChange={({ detail }) => setCreateForm(f => ({ ...f, targetCurrency: detail.value }))} placeholder="e.g. USD" />
                    </FormField>
                    <FormField label="OCM Mapping *">
                        <Input value={createForm.ocmMapping} onChange={({ detail }) => setCreateForm(f => ({ ...f, ocmMapping: detail.value }))} placeholder="e.g. DEFAULT" />
                    </FormField>
                    <FormField label="Value Date">
                        <Input value={createForm.valueDate} onChange={({ detail }) => setCreateForm(f => ({ ...f, valueDate: detail.value }))} placeholder="e.g. 2026-06-17" />
                    </FormField>
                    {createError && <Alert type="error">{createError}</Alert>}
                </SpaceBetween>
            </Modal>
        </SpaceBetween>
    );
};

export default ICSettlements;
